import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Typography } from "@material-ui/core";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import VisibilityOffRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";

import {
  tableCellRender,
  stringFilterOperators,
  getLatestUpdateAgro,
  twoDecimalsFormat,
  getRandomInt,
} from "../util/tableUtils";
import httpClient from "../../lib/httpClient";
import { ShowPerilsWithErrors } from "./ShowPerilsWithErrors";

export const ParcelsList = (props) => {
  const { t } = useTranslation();
  const policyNumber = props.policyNumber;
  const setCurrentlyVisiblePartitas = props.setCurrentlyVisiblePartitas;

  // Note: currently we have not decided whether to use original or V2 or V3 version or all them together

  const [parcelsV3, setParcelsV3] = useState([]);
  const [errorListv4, setErrorListv4] = useState([]); // list of strings, may be empty
  const [geometryErrors, setGeometryErrors] = useState([]);
  const [parcelsFetching, setParcelsFetching] = useState(false);
  const [lastUpdateDateMsg, setLastUpdateDateMsg] = useState("");

  const [pinnedSumRowV3, setPinnedSumRowV3] = useState({ _id: getRandomInt() });

  useEffect(() => {
    getLatestUpdateAgro("/serviceLogs/agriculture?service=agricultureDb_partite").then((lastUpdateMsg) => {
      setLastUpdateDateMsg(lastUpdateMsg);
    });
  }, []);

  const dictOfPartitasWithTrueValues = (data) => {
    const dataWithSelected = data
      .filter((parcel) => parcel.cadastralRecord)
      .map((parcel) => parcel.cadastralRecord)
      // Create an dict from list with values = true
      .reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {});
    // Typically {1:true, 2:true}
    return dataWithSelected;
  };

  useEffect(() => {
    const getPinnedSunRowV3 = (parcelsData) => {
      let compensationSum = 0,
        areaSum = 0,
        quintalsSum = 0;

      parcelsData.forEach((parcel) => {
        if (parcel.insuredValue) compensationSum += parcel.insuredValue;
        if (parcel.areaHectare) areaSum += parcel.areaHectare;
        if (parcel.weightQuintals) quintalsSum += parcel.weightQuintals;
      });

      const sumParcelRow = {
        _id: getRandomInt(),
        areaHectare: areaSum,
        insuredValue: compensationSum,
        weightQuintals: quintalsSum,
        cadastralRecord: t("table.sum"),
      };
      return sumParcelRow;
    };

    setParcelsFetching(true);
    if (policyNumber) {
      httpClient()
        .get(`/agriculturePolicies/${policyNumber}/parcelsV3`)
        .then((res) => res.data)
        .then(async (data) => {
          setPinnedSumRowV3(getPinnedSunRowV3(data));
          setCurrentlyVisiblePartitas(dictOfPartitasWithTrueValues(data));
          setParcelsV3(data);
          setParcelsFetching(false);
        });
    }
  }, [policyNumber, t, setCurrentlyVisiblePartitas]);

  useEffect(() => {
    if (policyNumber) {
      httpClient()
        .get(`/agriculturePolicies/${policyNumber}/parcelsErrorsV4`)
        .then((res) => res.data)
        .then(async (data) => {
          let errorList = [];
          data.forEach((element) => {
            if (element.sezioneAlts === "") {
              errorList.push(
                `ERROR E41: Partita ${element.cadastralRecord}: ${t("agriculture.cannotRecognize")} foglio="${
                  element.cadastralFoglio
                }" mappale="${element.cadastralMappale}"`,
              );
            }
          });
          const elementWhereMissingSezzioneDetected = data.find((element) => element.sezioneAlts !== "");

          if (elementWhereMissingSezzioneDetected) {
            errorList.push(
              `ERROR E42:   ${t("agriculture.sezioneMissing")}  (${
                elementWhereMissingSezzioneDetected.sezioneAlts
              })`,
            );
          }

          setErrorListv4(errorList);
        });
    }
  }, [policyNumber, t]);

  useEffect(() => {
    if (policyNumber) {
      httpClient()
        .get(`/agriculturePolicies/${policyNumber}/geometry`)
        .then((res) => res.data)
        .then(async (data) => {
          let errorList = [];
          data.policyGeo.forEach((element) => {
            element.errorMessages.forEach((messageRow) => {
              errorList.push(messageRow);
            });
          });

          setGeometryErrors(errorList);
        });
    }
  }, [policyNumber]);

  const onCheckboxChangeV3 = async (event, selectedRow) => {
    const isCheckboxChecked = event.target.checked;
    props.setCurrentlyVisiblePartitas((prev) => {
      const newValue = { ...prev, [selectedRow.cadastralRecord]: isCheckboxChecked };
      return newValue;
    });
  };

  const perilColumns = [
    {
      field: "cadastralRecord",
      headerName: t("agriculture.parcelNo"),
      valueGetter: (params) => {
        const currentCadastralRecord = params.row.cadastralRecord;

        // because of the pinned row label on the left side, the cadastralRecord can be a string as well
        return currentCadastralRecord && typeof currentCadastralRecord === "number"
          ? parseInt(currentCadastralRecord)
          : currentCadastralRecord;
      },
    },
    {
      field: "cadastralFoglio",
      headerName: t("agriculture.cadastralSheet"),
    },
    {
      field: "cadastralMappale",
      headerName: t("agriculture.cadastralMap"),
    },
    {
      field: "subalterno",
      headerName: t("agriculture.subalterno"),
    },
    {
      field: "plantVariety",
      headerName: t("agriculture.variety"),
    },
    {
      field: "plantVarietyDescription",
      headerName: t("agriculture.varietyDescription"),
    },
    {
      field: "areaHectare",
      headerName: t("agriculture.area"),
      sortable: true,
    },
    {
      field: "weightQuintals",
      headerName: t("agriculture.weight"),
    },
    {
      field: "plantName",
      headerName: t("agriculture.plant"),
    },
    {
      field: "unitYield",
      headerName: t("agriculture.unitYield"),
    },
    {
      field: "insuredValue",
      headerName: t("agriculture.insuredValue"),
      valueFormatter: (params) => {
        return params.value ? twoDecimalsFormat(params.value) : "-";
      },
      align: "right",
    },
  ];

  const columns = perilColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value),
    filterOperators: stringFilterOperators,
    flex: 1,
    ...tableColumn,
  }));

  const visibilitySelectorColumnsV3 = {
    field: "selected",
    headerName: t("policy.action"),
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const isInteger = Number.isInteger(params.row.cadastralRecord);

      return (
        isInteger && (
          <div onClick={(e) => e.stopPropagation()}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<VisibilityOffRoundedIcon />}
                  checkedIcon={<VisibilityRoundedIcon />}
                  checked={props.currentlyVisiblePartitas[params.row.cadastralRecord]}
                  onChange={(event) => onCheckboxChangeV3(event, params.row)}
                  color="primary"
                />
              }
            />
          </div>
        )
      );
    },
  };

  const errorv4 =
    errorListv4.length > 0 || geometryErrors.length > 0 ? (
      <div>
        {errorListv4.length > 0 && (
          <ul>
            {errorListv4.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )}
        {geometryErrors.length > 0 && (
          <ul>
            {geometryErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )}
      </div>
    ) : null;

  return (
    <Box>
      <Paper>
        <Box p={2} pt={2}>
          <Typography
            variant={"h2"}
            style={{
              fontSize: "1.3rem",
              fontWeight: 400,
            }}
          >
            {t("agriculture.parcelsTitle")}
          </Typography>
        </Box>
        <div style={{ maxHeight: "50vh", maxWidth: "100%", overflow: "auto" }}>
          <DataGridCystellarTable
            density={"compact"}
            columns={[
              visibilitySelectorColumnsV3,
              ...columns.filter((_, index) => index !== 1 && index !== 3),
            ]}
            rows={parcelsV3}
            rowCount={parcelsV3.length}
            getRowId={(rowData) => rowData._id}
            pagination={false}
            loading={parcelsFetching}
            pinnedRows={parcelsV3.length ? { bottom: [pinnedSumRowV3] } : {}}
            customToolbarComp={lastUpdateDateMsg ? true : false}
            customToolbarCompMsg={lastUpdateDateMsg}
            customToolbarWidgets={errorv4 ? <ShowPerilsWithErrors errorsText={errorv4} /> : ""}
          />
        </div>
      </Paper>
    </Box>
  );
};
